import {
  CreateUIAnalyticsEvent,
  UIAnalyticsEvent
} from '@atlaskit/analytics-next';
import { ProductKey, Storage } from '@atlassiansox/bxpkit-core';
import { SitesType } from '@atlassiansox/asc-stargate';
import { uniq } from 'lodash';
import { UserDataType } from '../types';

type BuildSolutionStatsEventType = {
  readonly createAnalyticsEvent: CreateUIAnalyticsEvent;
  readonly userInfo?: UserDataType;
  readonly selectedSite?: SitesType;
  readonly provisionedProducts?: string[];
  readonly targetWorkspaceId?: string;
  readonly aaid?: string | null;
};

/**
 * This function returns an analytics-next event for
 * tracking which products were provisioned vs existing,
 * as well as the primary cloudId & any existing instance IDs
 * in the cloud provisioning (or activateProducts) request.
 *
 * @param provisionedProducts - all products being provisioned in the request.
 * @param userInfo - userInfo from use-user-info hook
 * @param selectedSite - site that is selected (eg. applies to N2E existing sitepicker)
 * @param createAnalyticsEvent - analytics-next function, to be passed in from UI
 */

export function buildSolutionStatsEvent({
  provisionedProducts,
  userInfo,
  selectedSite,
  createAnalyticsEvent,
  targetWorkspaceId,
  aaid
}: BuildSolutionStatsEventType): UIAnalyticsEvent | undefined {
  const { sites } = userInfo || {};
  let existingProducts: string[] = [];
  let existingInstanceIds: string[] = [];

  if (sites) {
    sites.map((site) => {
      existingInstanceIds.push(site.cloudId);
      existingProducts = existingProducts.concat(site.products);
    });
  }

  const isN2NAtlassian = existingProducts.length === 0;
  const signupUrl = window?.location?.href || '';
  const targetCloudId = selectedSite?.cloudId || null;
  // TODO: could change pending workspace integration
  // const isNewBitbucketWorkspace = true;

  if (!provisionedProducts) {
    provisionedProducts = [
      ProductKey.JIRA_SOFTWARE,
      ProductKey.CONFLUENCE,
      ProductKey.BITBUCKET,
      ProductKey.OPSGENIE
    ];
  }

  // either existing sitepicker is not rendered, or a site
  // was selected without JSW included
  const isNewSiteProvisioned = !selectedSite;

  // N2N / N2E, not the classic definition - this definition
  // is whether or not JSW was provisioned
  const onboardingEmailCohort =
    isNewSiteProvisioned ||
    (selectedSite?.products &&
      !selectedSite?.products.includes(ProductKey.JIRA_SOFTWARE))
      ? 'N2N'
      : 'N2E';

  existingProducts = uniq(existingProducts);
  existingInstanceIds = uniq(existingInstanceIds);

  return createAnalyticsEvent({
    eventName: 'bxp.formCloudSignup.solutionStats',
    event: 'clicked',
    eventComponent: 'button',
    data: {
      anonymousId: Storage.getCookie('ajs_anonymous_id'),
      isN2NAtlassian,
      onboardingEmailCohort,
      signupUrl,
      existingProducts,
      provisionedProducts,
      existingInstanceIds,
      isNewSiteProvisioned,
      targetCloudId,
      targetWorkspaceId,
      aaid,
      provisionedCloudId: null
    }
  });
}
